/**
 * Texte de question porteur de formules.
 *
 * Le format est celui que le back stocke et que l'import produit : du texte
 * brut, avec de vrais sauts de ligne, dans lequel une formule s'insère en
 * LaTeX entre `\( … \)` (dans la ligne) ou `\[ … \]` (centrée). Une portion
 * entre `**` est mise en gras.
 *
 * Une question écrite avant les formules n'a aucun délimiteur : elle se lit
 * exactement comme avant. Le dollar n'est pas un délimiteur — « 5 $ » est un
 * prix avant d'être une formule.
 */

export interface MathSegment {
  /** Texte, ou LaTeX sans ses délimiteurs. */
  value: string;
  math: boolean;
  display: boolean;
  /** Position du fragment dans le texte source, délimiteurs compris. */
  start: number;
  end: number;
}

/** Découpe un texte en fragments texte / formule. Un délimiteur orphelin reste du texte. */
export function splitMath(value: string | null | undefined): MathSegment[] {
  const text = value ?? '';
  const segments: MathSegment[] = [];
  let buffer = '';
  let bufferStart = 0;
  let i = 0;

  const flush = (end: number) => {
    if (buffer.length > 0) segments.push({ value: buffer, math: false, display: false, start: bufferStart, end });
    buffer = '';
  };

  while (i < text.length) {
    if (text[i] === '\\' && (text[i + 1] === '(' || text[i + 1] === '[')) {
      const display = text[i + 1] === '[';
      const close = text.indexOf(display ? '\\]' : '\\)', i + 2);
      if (close >= 0) {
        flush(i);
        segments.push({ value: text.slice(i + 2, close), math: true, display, start: i, end: close + 2 });
        i = close + 2;
        bufferStart = i;
        continue;
      }
    }
    if (buffer.length === 0) bufferStart = i;
    buffer += text[i];
    i++;
  }

  flush(text.length);
  return segments;
}

export function hasMath(value: string | null | undefined): boolean {
  return splitMath(value).some(s => s.math);
}

/** Gras : `**texte**`, sans espace collé aux marques (« 2 ** 3 » n'en est pas). */
export const BOLD_PATTERN = /\*\*(?=\S)([\s\S]*?\S)\*\*/g;

export function hasBold(value: string | null | undefined): boolean {
  return !!value && new RegExp(BOLD_PATTERN.source).test(value);
}

/**
 * Vrai pour un texte HTML — les descriptions saisies autrefois dans un éditeur
 * riche. Elles restent rendues en HTML (assaini) ; seul le texte neuf suit le
 * format décrit plus haut.
 */
export function looksLikeHtml(value: string | null | undefined): boolean {
  return !!value && /<\/?(p|br|div|span|strong|b|em|i|u|s|ul|ol|li|h[1-6]|blockquote|sub|sup)\b[^>]*>/i.test(value);
}

// ── Texte lisible ───────────────────────────────────────────────────────────

const SYMBOLS: Record<string, string> = {
  alpha: 'α', beta: 'β', gamma: 'γ', delta: 'δ', epsilon: 'ε', varepsilon: 'ε', zeta: 'ζ', eta: 'η',
  theta: 'θ', vartheta: 'ϑ', iota: 'ι', kappa: 'κ', lambda: 'λ', mu: 'μ', nu: 'ν', xi: 'ξ', pi: 'π',
  rho: 'ρ', sigma: 'σ', tau: 'τ', upsilon: 'υ', phi: 'φ', varphi: 'φ', chi: 'χ', psi: 'ψ', omega: 'ω',
  Gamma: 'Γ', Delta: 'Δ', Theta: 'Θ', Lambda: 'Λ', Xi: 'Ξ', Pi: 'Π', Sigma: 'Σ', Upsilon: 'Υ',
  Phi: 'Φ', Psi: 'Ψ', Omega: 'Ω',
  times: '×', div: '÷', pm: '±', mp: '∓', cdot: '·', circ: '∘', leq: '≤', le: '≤', geq: '≥', ge: '≥',
  neq: '≠', ne: '≠', approx: '≈', simeq: '≃', cong: '≅', equiv: '≡', sim: '∼', propto: '∝',
  infty: '∞', partial: '∂', nabla: '∇', sum: '∑', prod: '∏', int: '∫', iint: '∬', oint: '∮',
  rightarrow: '→', to: '→', leftarrow: '←', leftrightarrow: '↔', Rightarrow: '⇒', implies: '⇒',
  Leftarrow: '⇐', Leftrightarrow: '⇔', iff: '⇔', longrightarrow: '⟶', mapsto: '↦',
  rightleftharpoons: '⇌', in: '∈', notin: '∉', subset: '⊂', supset: '⊃', subseteq: '⊆',
  supseteq: '⊇', cup: '∪', cap: '∩', emptyset: '∅', varnothing: '∅', setminus: '∖', forall: '∀',
  exists: '∃', neg: '¬', wedge: '∧', vee: '∨', ldots: '…', cdots: '⋯', dots: '…', hbar: 'ℏ',
  ell: 'ℓ', angle: '∠', perp: '⊥', parallel: '∥', langle: '⟨', rangle: '⟩', degree: '°',
  quad: ' ', qquad: '  ',
};

const FUNCTIONS = new Set([
  'sin', 'cos', 'tan', 'cot', 'arcsin', 'arccos', 'arctan', 'sinh', 'cosh', 'tanh', 'ln', 'log',
  'exp', 'lim', 'max', 'min', 'sup', 'inf', 'det', 'gcd', 'deg', 'arg',
]);

const SUPERSCRIPTS: Record<string, string> = {
  '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
  '+': '⁺', '-': '⁻', '=': '⁼', '(': '⁽', ')': '⁾', n: 'ⁿ', i: 'ⁱ', '∘': '°',
};

const SUBSCRIPTS: Record<string, string> = {
  '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉',
  '+': '₊', '-': '₋', '=': '₌', '(': '₍', ')': '₎', a: 'ₐ', e: 'ₑ', o: 'ₒ', x: 'ₓ', h: 'ₕ', k: 'ₖ',
  l: 'ₗ', m: 'ₘ', n: 'ₙ', p: 'ₚ', s: 'ₛ', t: 'ₜ', i: 'ᵢ', j: 'ⱼ', r: 'ᵣ', u: 'ᵤ', v: 'ᵥ',
};

const PASS_THROUGH = new Set([
  'text', 'mathrm', 'mathit', 'mathbf', 'mathsf', 'mathcal', 'operatorname', 'textbf', 'textit',
  'boxed', 'underline', 'underbrace', 'overbrace', 'mbox', 'ce', 'pu',
]);

/**
 * Lecture à plat d'une formule, pour les endroits où KaTeX ne peut pas
 * composer : infobulle, libellé d'accessibilité, boîte de confirmation,
 * recherche. `\(\mathrm{R}_{1}\)` devient « R₁ », `\(\frac{a+b}{2}\)` « (a+b)/2 ».
 */
export function toPlainText(value: string | null | undefined): string {
  return splitMath(value)
    .map(s => (s.math ? latexToPlain(s.value) : s.value.replace(BOLD_PATTERN, '$1')))
    .join('');
}

export function latexToPlain(latex: string): string {
  let pos = 0;

  const skipSpaces = () => {
    while (pos < latex.length && latex[pos] === ' ') pos++;
  };

  const wrap = (v: string) => {
    const t = v.trim();
    return t.length <= 1 || /^[\p{L}\p{N}.,′]+$/u.test(t) ? t : `(${t})`;
  };

  const script = (v: string, sup: boolean) => {
    const t = v.trim();
    const map = sup ? SUPERSCRIPTS : SUBSCRIPTS;
    if (!t) return '';
    if ([...t].every(c => map[c] !== undefined)) return [...t].map(c => map[c]).join('');
    return `${sup ? '^' : '_'}${wrap(t)}`;
  };

  const readArgument = (): string => {
    skipSpaces();
    if (pos >= latex.length) return '';
    if (latex[pos] === '{') {
      pos++;
      const inner = readSequence('}');
      pos++;
      return inner;
    }
    if (latex[pos] === '\\') return readCommand();
    return latex[pos++];
  };

  const readOptional = (): string | null => {
    skipSpaces();
    if (latex[pos] !== '[') return null;
    pos++;
    const inner = readSequence(']');
    pos++;
    return inner;
  };

  const readCommand = (): string => {
    pos++;
    if (pos >= latex.length) return '';
    if (!/[A-Za-z]/.test(latex[pos])) {
      const sign = latex[pos++];
      if (sign === '\\') return ' ; ';
      if (',;:! '.includes(sign)) return ' ';
      return sign === '|' ? '‖' : sign;
    }
    const start = pos;
    while (pos < latex.length && /[A-Za-z]/.test(latex[pos])) pos++;
    const name = latex.slice(start, pos);

    switch (name) {
      case 'frac': case 'dfrac': case 'tfrac': {
        const num = readArgument();
        const den = readArgument();
        return `${wrap(num)}/${wrap(den)}`;
      }
      case 'sqrt': {
        const degree = readOptional();
        const radicand = readArgument();
        return `${degree ? script(degree, true) : ''}√${wrap(radicand)}`;
      }
      case 'vec': case 'overrightarrow':
        return `${readArgument()}⃗`;
      case 'overline': case 'bar':
        return `${readArgument()}̅`;
      case 'hat': case 'widehat':
        return `${readArgument()}̂`;
      case 'mathbb': {
        const arg = readArgument();
        return ({ R: 'ℝ', N: 'ℕ', Z: 'ℤ', Q: 'ℚ', C: 'ℂ' } as Record<string, string>)[arg] ?? arg;
      }
      case 'left': case 'right': {
        skipSpaces();
        if (latex[pos] === '.') { pos++; return ''; }
        if (latex[pos] === '\\') return readCommand();
        return latex[pos++] ?? '';
      }
      case 'begin': case 'end':
        readArgument();
        if (name === 'begin' && latex[pos] === '{' && /^\{[lcr| ]+\}/.test(latex.slice(pos))) readArgument();
        return ' ';
    }

    if (PASS_THROUGH.has(name)) return readArgument();
    if (FUNCTIONS.has(name)) return `${name} `;
    return SYMBOLS[name] ?? name;
  };

  const readSequence = (stop: string | null): string => {
    let out = '';
    while (pos < latex.length) {
      const ch = latex[pos];
      if (stop !== null && ch === stop) return out;
      switch (ch) {
        case '{': pos++; out += readSequence('}'); pos++; break;
        case '}': pos++; break;
        case '^': case '_': pos++; out += script(readArgument(), ch === '^'); break;
        case '\\': out += readCommand(); break;
        case '&': case '~': pos++; out += ' '; break;
        default: pos++; out += ch;
      }
    }
    return out;
  };

  return readSequence(null).replace(/ {2,}/g, ' ').trim();
}
